import sanitizeHtml from "sanitize-html";
import { UserAssignee } from "@webapp/assignees/models/assignee.models";
import { marked } from "@webapp/lib/marked";
import { Search } from "@webapp/search/models/search.models";
import { GH_BLOT_ATTRIBUTES } from "@webapp/shared/rich-text-editor/blots/blot-attributes";
import { GH_BLOT_CLASSES } from "@webapp/shared/rich-text-editor/blots/blot-classes";

export const searchItemToUserAssignee = (user: Search<"users">): UserAssignee => ({
  id: user.id,
  type: "user",
  name: `${user.fields.firstName} ${user.fields.lastName}`,
  email: user.fields.auth0Cache.email,
  picture: user.fields.auth0Cache.picture,
});

export const splitTitleFromBulletList = (text: string): string => {
  const regex = new RegExp(/\n[*_~a-zA-Z]+/g);
  const matches = text.match(regex);

  if (matches) {
    let replaced = text;

    matches.forEach((m) => {
      replaced = replaced.replace(m, "\n\n" + m);
    });
    return replaced;
  }

  return text;
};

export const convertMarkdownToRichText = (text: string): string => {
  let trimmedTextWithReplacedNewLines = text.trim().replace(/^\n/gm, "\n<br>\n\n");
  trimmedTextWithReplacedNewLines = splitTitleFromBulletList(trimmedTextWithReplacedNewLines);

  const html = marked
    .parse(trimmedTextWithReplacedNewLines, {
      breaks: true,
    })
    .trim();

  return sanitizeRichTextHtml(html);
};

export const isGifOrEmojiPickerSearchFieldSelected = (el: Element): boolean => {
  const classesToExclude = ["search-gif", "emoji-picker__search"];

  return classesToExclude.some((classToExclude) => el?.classList?.contains?.(classToExclude));
};

/**
 * Sanitizes the HTML but allows specific classes and attributes related to the Rich text Editor
 *
 * @param html
 */
export const sanitizeRichTextHtml = (html: string): string => {
  return sanitizeHtml(html, {
    allowedClasses: { span: GH_BLOT_CLASSES },
    allowedAttributes: {
      a: ["href"],
      span: GH_BLOT_ATTRIBUTES,
    },
  });
};

/**
 * Uses the input string and parses the HTML, then return the `textContent`
 * This way escaped characters are converted back, e.g. `&amp;` -> `&`, `&lt;` -> `<`, but all HTML is stripped, e.g. script elements
 *
 * @param input
 */
export const stripHtmlElements = (input: string): string => {
  // DOMParser does NOT execute JS
  const doc = new DOMParser().parseFromString(input, "text/html");

  return doc.documentElement.textContent;
};

// Format is @[NAME:EMAIL:ID]
const mentionGroup = "([^:[\\]]+?)";
export const MENTION_PATTERN = `@\\[${mentionGroup}:${mentionGroup}:${mentionGroup}\\]`;
